<main class="exercise-library">
  <header class="library-hero">
    <div class="library-hero__copy">
      <div class="library-hero__eyebrow">
        <mat-icon>bolt</mat-icon>
        Exercise library
      </div>
      <h1>Build better sessions</h1>
      <p>
        Browse your exercise library by category, review the essentials at a
        glance, and keep your training content organized.
      </p>

      <div class="library-hero__stats" *ngIf="categoriesLoaded">
        <span>
          <strong>{{ totalExercises }}</strong>
          {{ totalExercises === 1 ? "exercise" : "exercises" }}
        </span>
        <span>
          <strong>{{ exerciseCategories?.length || 0 }}</strong>
          {{ exerciseCategories?.length === 1 ? "category" : "categories" }}
        </span>
      </div>
    </div>

    <button
      mat-flat-button
      class="create-exercise-button"
      [matMenuTriggerFor]="createMenu"
    >
      <mat-icon>add</mat-icon>
      Create exercise
      <mat-icon class="create-exercise-button__chevron"
        >keyboard_arrow_down</mat-icon
      >
    </button>
    <mat-menu #createMenu="matMenu">
      <a mat-menu-item routerLink="/save-exercise">
        <mat-icon>directions_run</mat-icon>
        <span>Single exercise</span>
      </a>
      <a mat-menu-item routerLink="/save-paired-exercise">
        <mat-icon>compare_arrows</mat-icon>
        <span>Paired exercise</span>
      </a>
    </mat-menu>
  </header>

  <section class="selection-bar" *ngIf="selectedExercises.length > 0">
    <div class="selection-bar__count">
      <span>{{ selectedExercises.length }}</span>
      selected
    </div>
    <div class="selection-bar__actions">
      <button
        type="button"
        class="bulk-action-button bulk-action-button--outline"
        (click)="openMoveModal()"
      >
        <mat-icon>drive_file_move</mat-icon>
        <span>Move to category</span>
      </button>
      <button
        type="button"
        class="bulk-action-button bulk-action-button--primary"
        *ngIf="isSuperAdmin()"
        (click)="openPushExercisesDialog()"
      >
        <mat-icon>cloud_upload</mat-icon>
        <span>Push to location</span>
      </button>
    </div>
  </section>

  <section class="loading-state" *ngIf="isLoading || !categoriesLoaded">
    <mat-spinner diameter="44"></mat-spinner>
    <p>Loading your exercise library…</p>
  </section>

  <section
    class="empty-library"
    *ngIf="
      !isLoading &&
      categoriesLoaded &&
      (!exerciseCategories || exerciseCategories.length === 0)
    "
  >
    <div class="empty-library__icon">
      <mat-icon>fitness_center</mat-icon>
    </div>
    <h2>Your library is ready for its first exercise</h2>
    <p>
      Create a single or paired exercise to start building training sessions.
    </p>
    <button
      mat-flat-button
      class="create-exercise-button"
      [matMenuTriggerFor]="createMenu"
    >
      <mat-icon>add</mat-icon>
      Create exercise
    </button>
  </section>

  <section
    class="library-toolbar"
    *ngIf="
      !isLoading &&
      categoriesLoaded &&
      exerciseCategories &&
      exerciseCategories.length > 0
    "
  >
    <label class="exercise-search">
      <mat-icon>search</mat-icon>
      <input
        type="search"
        aria-label="Search exercises and categories"
        placeholder="Search by exercise or category"
        [(ngModel)]="searchQuery"
      />
      <button
        type="button"
        class="exercise-search__clear"
        aria-label="Clear search"
        *ngIf="hasSearchQuery"
        (click)="clearSearch()"
      >
        <mat-icon>close</mat-icon>
      </button>
    </label>

    <span class="library-toolbar__result" *ngIf="hasSearchQuery">
      {{ visibleExerciseTotal }}
      {{ visibleExerciseTotal === 1 ? "exercise" : "exercises" }} found
    </span>
  </section>

  <section
    class="empty-search"
    *ngIf="
      !isLoading &&
      categoriesLoaded &&
      hasSearchQuery &&
      visibleCategories.length === 0
    "
  >
    <mat-icon>search_off</mat-icon>
    <h2>No matching exercises</h2>
    <p>Try another exercise name or category.</p>
    <button mat-stroked-button (click)="clearSearch()">Clear search</button>
  </section>

  <div
    class="category-list"
    *ngIf="
      !isLoading &&
      categoriesLoaded &&
      exerciseCategories &&
      exerciseCategories.length > 0 &&
      visibleCategories.length > 0
    "
  >
    <section
      class="category-section"
      [class.category-section--collapsed]="
        !isCategoryContentVisible(category.id)
      "
      *ngFor="let category of visibleCategories; trackBy: trackByCategoryId"
    >
      <div class="category-section__header">
        <div class="category-section__title">
          <button
            mat-icon-button
            class="category-section__collapse"
            [disabled]="hasSearchQuery"
            [attr.aria-expanded]="isCategoryContentVisible(category.id)"
            [attr.aria-controls]="'category-content-' + category.id"
            [attr.aria-label]="
              isCategoryContentVisible(category.id)
                ? 'Collapse ' + category.name
                : 'Expand ' + category.name
            "
            (click)="toggleCategory(category.id)"
          >
            <mat-icon>
              {{
                isCategoryContentVisible(category.id)
                  ? "expand_less"
                  : "expand_more"
              }}
            </mat-icon>
          </button>
          <h2>{{ category.name }}</h2>
          <span class="category-section__count">
            {{ getVisibleExerciseCount(category) }}
            {{
              getVisibleExerciseCount(category) === 1 ? "exercise" : "exercises"
            }}
          </span>
        </div>

        <div class="category-section__actions" *ngIf="isSuperAdmin()">
          <mat-checkbox
            color="primary"
            [checked]="isCategoryFullySelected(category.id)"
            [indeterminate]="isCategoryPartiallySelected(category.id)"
            [disabled]="!exercises[category.id]?.length"
            (change)="addCategory(category.id)"
          >
            Select all
          </mat-checkbox>

          <button
            mat-icon-button
            aria-label="Category actions"
            *ngIf="hasExercisesSelected(category.id)"
            [matMenuTriggerFor]="categoryMenu"
          >
            <mat-icon>more_horiz</mat-icon>
          </button>
          <mat-menu #categoryMenu="matMenu">
            <button mat-menu-item (click)="openPushExercisesDialog()">
              <mat-icon>cloud_upload</mat-icon>
              <span>Push selected to location</span>
            </button>
          </mat-menu>
        </div>
      </div>

      <div
        class="category-section__content"
        [id]="'category-content-' + category.id"
        *ngIf="isCategoryContentVisible(category.id)"
      >
        <app-category-exercises
          *ngIf="exercises[category.id]?.length > 0"
          [exercises]="exercises[category.id]"
          [exercisesSelected]="selectedExercises"
          [category]="category"
          [searchQuery]="searchQuery"
        ></app-category-exercises>

        <div
          class="empty-category"
          *ngIf="!exercises[category.id] || exercises[category.id].length === 0"
        >
          <mat-icon>add_circle_outline</mat-icon>
          <div>
            <strong>No exercises in this category yet</strong>
            <span
              >Create an exercise and assign it to {{ category.name }}.</span
            >
          </div>
        </div>
      </div>
    </section>
  </div>
</main>
